/**
 * JOProPricingInjector — Automatically swaps retail (TTC) prices to Pro (HT)
 * prices on all product cards / product detail pages when the logged-in
 * customer is flagged is_pro=true.
 *
 * Also injects a small "PRIX PRO HT" pill next to each price so the user knows
 * they're seeing their reserved tariff.
 *
 * NON-CORE — additive injector. Reads from /api/shop/products
 * (attributes.price_pro_ht_cents) and matches product cards by their
 * data-testid="shop-product-<slug>" markers.
 *
 * Mounted globally in App.js. Only activates on:
 *   - /shop, /boutique, /shop/*
 *   - /product/:slug, /produit/:slug
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "msc_customer_token";
const PILL_MARKER = "jo-pro-price-pill";
const PRICE_MARKER = "data-jo-pro-price-applied";

function isShopRoute(pathname) {
  return (
    pathname === "/shop" ||
    pathname.startsWith("/shop/") ||
    pathname === "/boutique" ||
    pathname.startsWith("/boutique/")
  );
}

function fmtEur(cents) {
  return `${(Number(cents) / 100).toFixed(2)} €`;
}

function ensurePill(priceEl, priceCents) {
  // Inject or update a small "PRO HT" pill after the price
  let pill = priceEl.parentElement?.querySelector(`.${PILL_MARKER}`);
  if (!pill) {
    pill = document.createElement("span");
    pill.className = PILL_MARKER;
    pill.style.cssText = `
      display:inline-block;
      margin-left:6px;
      padding:2px 7px;
      border-radius:9999px;
      font-size:9px;
      font-weight:700;
      letter-spacing:.08em;
      text-transform:uppercase;
      background:#2d6a4f;
      color:#fff;
      vertical-align:middle;
      white-space:nowrap;
    `;
    pill.textContent = "Prix Pro HT";
    priceEl.parentElement?.appendChild(pill);
  }
  priceEl.setAttribute(PRICE_MARKER, String(priceCents));
}

function applyProPricing(products) {
  // Build slug → price_pro_ht_cents map (now at top level, was in attributes)
  const proBySlug = new Map();
  for (const p of products) {
    const pro = Number(p?.price_pro_ht_cents || p?.attributes?.price_pro_ht_cents || 0);
    if (pro > 0) proBySlug.set(p.slug, pro);
  }
  if (proBySlug.size === 0) return;

  // A) Shop cards: [data-testid="shop-product-<slug>"]
  document.querySelectorAll('[data-testid^="shop-product-"]').forEach((card) => {
    const testid = card.getAttribute("data-testid") || "";
    const slug = testid.replace(/^shop-product-/, "");
    const pro = proBySlug.get(slug);
    if (!pro) return;

    // Find first price-like span (contains € character)
    const spans = card.querySelectorAll("span");
    for (const span of spans) {
      const t = (span.textContent || "").trim();
      if (!/€/.test(t)) continue;
      // ignore already-processed
      if (span.getAttribute(PRICE_MARKER) === String(pro)) return;
      // Skip line-through (compare_at price)
      const cs = window.getComputedStyle(span);
      if (cs.textDecorationLine.includes("line-through")) continue;
      span.textContent = fmtEur(pro);
      ensurePill(span, pro);
      return; // one price per card
    }
  });

  // B) Product detail: URL is /shop/<slug>. Look for a big price container.
  const path = window.location.pathname;
  const detailMatch = path.match(/^\/shop\/([^/?#]+)$/);
  if (detailMatch) {
    const slug = detailMatch[1];
    const pro = proBySlug.get(slug);
    if (pro) {
      // Look for the primary price display: any element with data-testid containing "price"
      // or a large font-bold text containing €
      const candidates = document.querySelectorAll(
        '[data-testid*="price"], .product-price, [class*="price"]'
      );
      for (const el of candidates) {
        const t = (el.textContent || "").trim();
        if (!/€/.test(t)) continue;
        if (el.getAttribute(PRICE_MARKER) === String(pro)) continue;
        // Only rewrite leaf-like small elements to avoid trampling children
        if (el.children.length === 0) {
          el.textContent = fmtEur(pro);
          ensurePill(el, pro);
        }
      }
    }
  }
}

function removeAllPillsAndReset() {
  document.querySelectorAll(`.${PILL_MARKER}`).forEach((n) => n.remove());
  document.querySelectorAll(`[${PRICE_MARKER}]`).forEach((n) => n.removeAttribute(PRICE_MARKER));
}

export default function JOProPricingInjector() {
  const location = useLocation();
  const [customer, setCustomer] = useState(null);
  const [products, setProducts] = useState([]);

  // Fetch current customer once
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY) || "";
    if (!token) {
      setCustomer(null);
      return;
    }
    let alive = true;
    axios
      .get(`${API}/customer/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (alive) setCustomer(r.data); })
      .catch(() => { if (alive) setCustomer(null); });
    return () => { alive = false; };
    // Re-fetch on any route change (in case they just logged in/out)
  }, [location.pathname]);

  // Fetch products when we know we need Pro pricing
  useEffect(() => {
    if (!customer?.is_pro) {
      setProducts([]);
      removeAllPillsAndReset();
      return;
    }
    if (!isShopRoute(location.pathname)) return;
    let alive = true;
    axios
      .get(`${API}/shop/products`)
      .then((r) => { if (alive) setProducts(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [customer, location.pathname]);

  // Apply/reapply pricing whenever products or route changes
  useEffect(() => {
    if (!customer?.is_pro || !isShopRoute(location.pathname) || products.length === 0) {
      return;
    }
    const run = () => applyProPricing(products);
    run();
    // Retry a few times because cards can mount lazily
    let tries = 0;
    const interval = setInterval(() => {
      tries++;
      run();
      if (tries > 15) clearInterval(interval);
    }, 300);

    // Observe for late DOM changes (sort, filter, pagination)
    const observer = new MutationObserver(() => run());
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, [customer, products, location.pathname]);

  // Cleanup when leaving shop area
  useEffect(() => {
    if (!isShopRoute(location.pathname)) removeAllPillsAndReset();
  }, [location.pathname]);

  return null;
}
